// OWNER: FRONTEND-ADMIN. Pure calendar helpers (no DOM). Report days/weeks and number ramp days are counted in
// OPS_TZ (wrangler.toml var; SPEC §6.1, §12). Dates are 'YYYY-MM-DD' strings.

export const OPS_TZ = 'America/Toronto';

/** Calendar date of an instant in a time zone, 'YYYY-MM-DD'. */
export function ymdInTz(d: Date, tz: string = OPS_TZ): string {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

function toUtc(ymd: string): Date {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}
const fromUtc = (d: Date): string => d.toISOString().slice(0, 10);

export function addDays(ymd: string, n: number): string {
  const d = toUtc(ymd);
  d.setUTCDate(d.getUTCDate() + n);
  return fromUtc(d);
}

/** Whole calendar days from a to b (b - a). */
export function daysBetween(a: string, b: string): number {
  return Math.round((toUtc(b).getTime() - toUtc(a).getTime()) / 86400000);
}

/** 0 = Sunday ... 6 = Saturday. */
export const weekday = (ymd: string): number => toUtc(ymd).getUTCDay();

export function mondayOf(ymd: string): string {
  const wd = weekday(ymd);
  return addDays(ymd, wd === 0 ? -6 : 1 - wd);
}

/** The Monday after the given date's week (for "clone to next week"). */
export function nextMonday(ymd: string): string {
  return addDays(mondayOf(ymd), 7);
}

export type RangePreset = 'today' | 'this_week' | 'last_week' | 'last_4_weeks' | 'this_month' | 'last_30_days';
export const RANGE_PRESETS: Array<{ key: RangePreset; label: string }> = [
  { key: 'today', label: 'Today' },
  { key: 'this_week', label: 'This week' },
  { key: 'last_week', label: 'Last week' },
  { key: 'last_4_weeks', label: 'Last 4 weeks' },
  { key: 'this_month', label: 'This month' },
  { key: 'last_30_days', label: 'Last 30 days' },
];

/** Inclusive from/to dates for a preset, relative to `today` (an OPS_TZ date). Weeks start Monday (ISO). */
export function presetRange(preset: RangePreset, today: string): { from: string; to: string } {
  switch (preset) {
    case 'today': return { from: today, to: today };
    case 'this_week': return { from: mondayOf(today), to: today };
    case 'last_week': { const m = addDays(mondayOf(today), -7); return { from: m, to: addDays(m, 6) }; }
    case 'last_4_weeks': return { from: addDays(mondayOf(today), -21), to: today };
    case 'this_month': return { from: today.slice(0, 8) + '01', to: today };
    case 'last_30_days': return { from: addDays(today, -29), to: today };
  }
}

/** SPEC §6.1 ramp day: OPS_TZ calendar days since the ramp started (0 on the first day). */
export function rampDay(rampStartedAt: string | null, now: Date, tz: string = OPS_TZ): number | null {
  if (!rampStartedAt) return null;
  const start = new Date(rampStartedAt);
  if (Number.isNaN(start.getTime())) return null;
  return Math.max(0, daysBetween(ymdInTz(start, tz), ymdInTz(now, tz)));
}

const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** '2026-11-26' → 'Thu Nov 26, 2026' */
export function prettyYmd(ymd: string): string {
  const d = toUtc(ymd);
  return `${WEEKDAY_SHORT[d.getUTCDay()]} ${MONTH_SHORT[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
}

/** Compact date for dense tables: 'Sep 2' in the current year, "Sep '25" otherwise (the full date goes in a tooltip). */
export function shortYmd(ymd: string, today: string): string {
  if (!ymd) return '';
  const d = toUtc(ymd);
  const month = MONTH_SHORT[d.getUTCMonth()];
  return ymd.slice(0, 4) === today.slice(0, 4) ? `${month} ${d.getUTCDate()}` : `${month} '${ymd.slice(2, 4)}`;
}

/** ISO-8601 instant → 'YYYY-MM-DD' in OPS_TZ for date inputs; '' when null. */
export function isoToYmd(iso: string | null | undefined, tz: string = OPS_TZ): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : ymdInTz(d, tz);
}

/** A date input value ('YYYY-MM-DD') → ISO instant at noon UTC (stable across zones); null when empty. */
export function ymdToIso(ymd: string): string | null {
  return ymd ? `${ymd}T12:00:00.000Z` : null;
}
